import type { CSSProperties } from 'react';

import type { PreviewVenue } from '../../../../api/regions';

interface Props {
  toRegionName: string;
  affected: PreviewVenue[];
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(20,28,22,0.4)',
  padding: 24,
};
const cardStyle: CSSProperties = {
  width: 560,
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
};
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 0',
  borderBottom: '1px solid var(--nb-border)',
};
const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  padding: '14px 22px',
  borderTop: '1px solid var(--nb-border)',
};

/**
 * Confirm-before-save reassignment modal (BON-153). Lists every venue that
 * would change region as a result of the composition edit; saving is gated on
 * the editor okaying the move. The one-time backfill reuses this same review.
 */
export function ReassignConfirmModal({
  toRegionName,
  affected,
  saving,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm venue reassignment"
      style={overlayStyle}
    >
      <div className="nb-card" style={cardStyle}>
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--nb-border)' }}>
          <h2
            className="nb-display"
            style={{ margin: 0, fontSize: 18, color: 'var(--nb-bark-dark)' }}
          >
            Confirm venue reassignment
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--nb-muted)' }}>
            {affected.length === 0 ? (
              <>No venues change region with this composition. Save to apply.</>
            ) : (
              <>
                <strong>{affected.length}</strong> venue{affected.length === 1 ? '' : 's'} will move
                into <strong>{toRegionName}</strong>. Review before saving.
              </>
            )}
          </p>
        </div>

        {affected.length > 0 && (
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 22px' }}>
            {affected.map((v) => (
              <div key={v.id} style={rowStyle}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nb-ink)' }}>
                    {v.name}
                  </div>
                  <div className="nb-mono" style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>
                    {v.address}
                  </div>
                </div>
                <span style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>
                  {v.fromRegionName ?? 'Unassigned'}
                </span>
                <span style={{ color: 'var(--nb-faint)' }}>→</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--nb-ink)' }}>
                  {v.toRegionName}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={footerStyle}>
          <button className="nb-btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="nb-btn nb-btn-primary" onClick={onConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm & save'}
          </button>
        </div>
      </div>
    </div>
  );
}
